import * as Sentry from "@sentry/react";
import { Method } from "axios";
import { showGlobalModal } from "./state/generalActions";
import TEXT, { ErrorType } from "./text-consts";
import { UNKNOWN_API_ERROR_PREFIX, UnmappedThirdPartyValue } from "../../../constants";
import { isRunningOnClientSide } from "../../../shared/util";

// @ts-expect-error: might return function for CODE_NOT_REGISTRATED
const mapValidationErrorCode = (status: ErrorType): string | undefined => TEXT.ERROR[status];

const supportPhoneNumber =
    isRunningOnClientSide() && window.nettbutikkConfig ? window.nettbutikkConfig.supportPhoneNumber : "92405050";

const isRetailer = isRunningOnClientSide() && !!window.__INITIAL_STATE?.general?.isRetailer;

// On the client, jQuery is included, so window.$ is defined. Server, or tests, use an empty fn.
let makeAjaxCall: <R>(req?: Request<R>) => void = function () {};
if (isRunningOnClientSide()) {
    if (window.$ && window.$.ajax) {
        makeAjaxCall = window.$.ajax;
    } else {
        // oxlint-disable-next-line no-console
        console.warn("Ajax: Warning - window.$.ajax isn't available, disabling Ajax calls");
    }
}

type DataType = "json" | "JSONP";

type RequestOptions = {
    data?: object;
    dataType?: DataType;
    retry?: boolean;
    url: string;
    xhrFields?: object;
};

type Dispatch = (action: object) => void;

type ResponseJSON = {
    code: unknown;
    displayable: boolean;
    error: ErrorType;
    /** Optional severity of the displayable error, e.g. "info"/"warning" to show it as a notice/warning instead of an error. */
    severity?: "error" | "info" | "warning";
};

type Xhr = {
    responseJSON?: ResponseJSON;
    responseText: string;
    status: number;
};
type IErrorType = "error" | UnmappedThirdPartyValue<"errorType">;
interface ErrorOptions {
    data?: object;
    durationMs?: number;
    errorThrown?: Error;
    errorType?: IErrorType;
    method?: Method;
    url?: string;
    xhr?: Xhr;
}

type Request<R> = {
    contentType?: string;
    data?: string;
    dataType: DataType;
    error: (xhr: Xhr, errorType: IErrorType, errorThrown: Error) => void;
    success: (value: PromiseLike<R> | R) => void;
    type: Method;
    url: string;
    xhrFields: object;
};

type TroubleshootingProps = {
    data?: object;
    durationMs?: number;
    errorThrown?: Error;
    errorType?: IErrorType;
    method?: Method;
    response?: ResponseJSON;
    url?: string;
};

function sendRequest<R>(method: Method, options: RequestOptions) {
    const targetUrl = options.url;
    if (!targetUrl) {
        throw new Error("url is a required option");
    }
    return new Promise<R>(function (resolve, reject) {
        const startedMs = Date.now();
        makeAjaxCall(
            createRequest<R>(method, options, resolve, (xhr, errorType, errorThrown) => {
                const durationMs = Date.now() - startedMs;
                const errorOptions: ErrorOptions = {
                    xhr,
                    errorType,
                    errorThrown,
                    method,
                    url: targetUrl,
                    durationMs,
                    data: options.data,
                };
                const err = new AjaxErrorInfo(errorOptions);
                reject(err);
                reportError(err);
            }),
        );
    });
}

function createRequest<R>(
    method: Method,
    options: RequestOptions,
    onSuccess: Request<R>["success"],
    onError: Request<R>["error"],
) {
    const request: Request<R> = {
        type: method,
        url: options.url,
        dataType: options.dataType || "json",
        data: options.data ? JSON.stringify(options.data) : undefined,
        xhrFields: options.xhrFields || {},
        success: onSuccess,
        error: onError,
    };

    if (method === "POST" || method === "PUT") {
        request.contentType = "application/json"; // contentType not allowed for GET by CORS headers
    }

    return request;
}

//--------------------------------------------------- reporting & progress tracking

/** Sent via reject when an Ajax call fails. */
class AjaxErrorInfo extends Error {
    readonly code: unknown;
    readonly displayableMessage?: string;
    readonly isGenericMessage: boolean;
    readonly severity?: "error" | "info" | "warning";
    displayable: boolean;
    readonly error?: string;
    readonly httpStatus?: number;
    troubleshooting?: TroubleshootingProps;

    constructor(options: ErrorOptions) {
        super();
        const responseJSON = options.xhr ? options.xhr.responseJSON : undefined;
        let backendErrorMessage: string | undefined;
        let displayable = false;
        let defaultDisplayableError: string | undefined = isRetailer
            ? "Beklager, en ukjent feil har oppstått."
            : `Beklager, en ukjent feil har oppstått. Prøv igjen, eller ta kontakt med oss på telefon ${supportPhoneNumber} for å få hjelp.`;

        if (responseJSON && responseJSON.displayable) {
            backendErrorMessage = responseJSON.error;
            displayable = responseJSON.displayable;
            this.code = responseJSON.code;
            this.severity = responseJSON.severity || undefined;
        } else if (responseJSON) {
            const logicalErrorMessage = mapValidationErrorCode(responseJSON.error);
            if (logicalErrorMessage) {
                defaultDisplayableError = logicalErrorMessage;
            } else if (!responseJSON.error?.startsWith(UNKNOWN_API_ERROR_PREFIX)) {
                Sentry.captureException(new Error(responseJSON.error), (scope) => {
                    scope.setTag("errorType", "Missing mapping of error code");
                    scope.setExtra("responseJSON", responseJSON);
                    return scope;
                });
            }
        }

        /** displayableMessage is always set and always can be shown to the user. */
        this.displayableMessage = displayable ? backendErrorMessage : defaultDisplayableError;
        /** Indicates that the displayable error is a generic one and thus a more concrete generic error (e.g. checkout-specific) might be better. */
        this.isGenericMessage = !displayable;

        /** Deprecated - use .displayableMessage (and possibly .isGenericMessage) instead  */
        {
            this.displayable = displayable;
            this.error = backendErrorMessage;
            this.httpStatus = options.xhr?.status;
        }

        setTroubleshootingDetails(this, responseJSON, options);
    }
}

function setTroubleshootingDetails(
    instance: AjaxErrorInfo,
    maybeResponseJSON?: ResponseJSON,
    options: ErrorOptions = {},
) {
    // URL unique to the endpoint, without request-specific data
    const endpointUrl = (options.url || "") // URL might be undefined e.g. when a JSON parsing exception occurres
        .replace(/\/api\//, "") // prefix common to all
        .replace(/\?.*/, "") // rm query string, it has often request-specific data
        .replace(/(\/api\/checkout\/checkPhoneNumber\/).*/, "$1"); // rm the tlf number, we don't want a separate error name for each one

    const xhr = options.xhr;
    const errorType = options.errorType; // f.ex. "error"
    const errorThrown = options.errorThrown; // f.ex. "Internal Server Error" when status=500
    // Some possible errors:
    // - Timeout: errorType === "timeout"
    // - Server error: errorType === "error" && xhr.status >= 500
    // - Request not sent (because the user left the page?) - see isUnsent
    const method = options.method;
    const url = options.url;

    /** User left the page before the request could have been sent, happens regularly */
    const isUnsent = errorType === "error" && xhr?.status === 0;
    const statusStr = isUnsent ? "UNSENT=user left page or not allowed" : xhr?.status;

    instance.name = `AjaxError_${method}_${endpointUrl}_${xhr?.status}`;
    instance.message =
        `Ajax ${method} => ${errorType} (status ${statusStr}): ` +
        `${errorThrown}, url: ${url}, body: ${xhr?.responseText}`;

    instance.troubleshooting = {
        errorType,
        errorThrown,
        method,
        url,
        data: options.data,
        durationMs: options.durationMs,
        response: maybeResponseJSON,
    };
}

// oxlint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
function reportError(ajaxErrorInfo: AjaxErrorInfo) {
    // errorType is one of ["timeout", "error", "abort", "parsererror"]
    // xhr.status:
    // - If the state is UNSENT or OPENED, return 0 (statusText = "")
    //   (UNSENT = not allowed to be performed)
    // - If the error flag is set, return 0 (statusText = "")
    // - Else Return the HTTP status code.
    // Note: 'The error flag indicates some type of network error or fetch termination.'
    // function stripTimestamp(url) {
    //     return url.replace(/(\?|&)ts=\d+/, "");
    // }
    /* DO NOTHING - we already report them on the server side (otherwise we'd use window.Raven.captureException) */
}

//--------------------------------------------------- utils

function ajaxErrorHandler(err: AjaxErrorInfo, dispatch: Dispatch = () => {}): never {
    if (err.httpStatus === 401) {
        window.location.assign("/nexus/");
        throw err; // navigerer bort, men TypeScript krever at vi kaster
    } else if (err.code === "LockedOrder") {
        // This error can happen for many different calls at different places
        // and we want to have just a single place to handle that so we do it here
        err.displayable = false; // We use the modal to display it so skip the normal error flash
        dispatch(showGlobalModal("lockedOrder"));
    } else if (err.code === "InvalidOrder") {
        err.displayable = false;
        dispatch(showGlobalModal("invalidOrder"));
    } else if (err.code === "NoneOrder") {
        err.displayable = false;
        dispatch(showGlobalModal("noneOrder"));
    } else if (err.code === "FinishedOrderLackingSubmit") {
        // @ts-expect-error: allow redirect
        window.location = "/shop/kasse/resubmit/";
    }

    throw err;
}

function defaultRejection() {
    return Promise.reject(new Error("Ajax not available - not running on client-side."));
}
const checkAndSendRequest = <R>(method: Method, options: RequestOptions, dispatch?: Dispatch) => {
    if (!isRunningOnClientSide()) {
        return defaultRejection();
    }
    return sendRequest<R>(method, options).catch((err) => ajaxErrorHandler(err, dispatch));
};

export const get: <R = void>(options: RequestOptions, dispatch?: Dispatch) => Promise<R> = (options, dispatch) =>
    checkAndSendRequest("GET", options, dispatch);
export const post: <R = void>(options: RequestOptions, dispatch?: Dispatch) => Promise<R> = (options, dispatch) =>
    checkAndSendRequest("POST", options, dispatch);
export const remove: <R = void>(options: RequestOptions, dispatch?: Dispatch) => Promise<R> = (options, dispatch) =>
    checkAndSendRequest("DELETE", options, dispatch);
export const put: <R = void>(options: RequestOptions, dispatch?: Dispatch) => Promise<R> = (options, dispatch) =>
    checkAndSendRequest("PUT", options, dispatch);
